import * as React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslations } from "../i18n/useTranslations";
import { getMobileDevice, isBrowser, stopPropagation } from "../utils";
import { useSearch } from "./useSearch";
import { PopularItems } from "./PopularItems/PopularItems";
import { SearchHistory } from "./SearchHistory/SearchHistory";
import { SearchSuggestions } from "./Suggestions/SearchSuggestions";
import { SearchIcon } from "../icons/SearchIcon";
import { BackIcon } from "../icons/BackIcon";
import { useStateWithLocalStorage as useLocalStorage } from "../hooks/useStateWithLocalStorage/useStateWithLocalStorage";

type KeydownEvent = React.KeyboardEvent<HTMLInputElement>;

export const Search = () => {
  if (!isBrowser()) return null;

  const { isMobile, isAndroid, isIPhone } = getMobileDevice(navigator);
  const { t } = useTranslations();

  const elRef = useRef<HTMLDivElement>();
  const searchRef = useRef<HTMLButtonElement>();
  const inputRef = useRef<HTMLInputElement>();
  const clearRef = useRef<HTMLButtonElement>();
  const cancelRef = useRef<HTMLButtonElement>();

  const {
    onInputChange,
    onInputFocus,
    onInputBlur,
    query,
    isInputFocused,
    hasQuery,
    closeSearch,
    clear,
    search,
    suggestions
  } = useSearch();

  const [searchHistory, storeHistory] = useLocalStorage<string[]>("dd.searchHistory", []);

  const storeSearchHistory = useCallback(() => {
    if (hasQuery) storeHistory([...new Set([query, ...searchHistory])]);
  }, [hasQuery, query, searchHistory, storeHistory]);

  const handleStopPropagation = useCallback(stopPropagation, [stopPropagation]);

  const handleSearch = useCallback(
    (e: MouseEvent) => {
      if (hasQuery) {
        storeSearchHistory();
        search();
      } else {
        e.stopPropagation();
      }
    },
    [search, hasQuery, storeSearchHistory]
  );

  // clears the input text
  const handleClear = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      clear();
      inputRef.current?.focus();
    },
    [clear]
  );

  const handleCancel = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      clear();
      inputRef.current?.blur();
    },
    [clear]
  );

  // animates out the search and close it
  const handleCloseSearch = useCallback(() => closeSearch(), [closeSearch]);

  // when the user press "Enter" it performs a search
  // but if press "Escape" it clears leaving the text empty
  const onKeyDown = (e: KeydownEvent) => {
    if (e.key === "Enter") {
      storeSearchHistory();
      clear();
      closeSearch();
      search();
    } else if (e.key === "Escape") {
      clear();
      if (!hasQuery) {
        handleCloseSearch();
      }
    }
  };

  useLayoutEffect(() => {
    elRef.current?.classList.toggle("elm-opacity-0", false);
  }, [isMobile]);

  useEffect(() => {
    const el = searchRef.current;
    el?.addEventListener("click", handleSearch);
    return () => el?.removeEventListener("click", handleSearch);
  }, [handleSearch, searchRef]);

  useEffect(() => {
    const el = cancelRef.current;
    el?.addEventListener("click", handleCancel);
    return () => {
      el?.removeEventListener("click", handleCancel);
    };
  }, [handleCancel]);

  useEffect(() => {
    const el = clearRef.current;
    el.addEventListener("click", handleClear);
    return () => el.removeEventListener("click", handleClear);
  }, [handleClear]);

  useEffect(() => {
    // don't close the search if we click inside
    const el = elRef.current;
    el?.addEventListener("click", handleStopPropagation);
    return () => el?.removeEventListener("click", handleStopPropagation);
  }, [handleStopPropagation]);

  const hasQueryClass = hasQuery ? "elm-opacity-100" : "elm-opacity-0";
  const isHistoryVisible = isInputFocused && !hasQuery;
  const hidePopularItems = (isAndroid || isIPhone) && (isHistoryVisible || hasQuery);
  const hasFocusOrQuery = isInputFocused || hasQuery;

  return (
    <div className="elm-absolute elm-bg-white elm-px-6 elm-pt-20 elm-pb-6 elm-w-full elm-z-10 md:elm-min-h-0 elm-min-h-screen sm:elm-min-h-screen lg:elm-min-h-0 xl:elm-min-h-0 elm-h-screen lg:elm-h-auto elm-overflow-auto lg:elm-overflow-visible">
      <div
        ref={elRef}
        role="searchbox"
        aria-label={t("Search")}
        className="elm-relative elm-opacity-0 elm-transition-all elm-duration-500 elm-flex elm-items-top elm-max-w-md elm-mx-auto"
      >
        <div className="elm-absolute elm-w-full">
          <div
            className={
              "elm-w-full md:elm-bg-white md:elm-border md:elm-border-gray-300 md:elm-border-solid md:elm-rounded-3xl " +
              (hasFocusOrQuery ? "md:elm-shadow-md" : "")
            }
          >
            <div
              className={
                "lg:focus:elm-outline-none elm-flex elm-items-center elm-w-full elm-border elm-border-gray-300 elm-border-solid elm-rounded-full elm-p-1.5 md:elm-shadow-none md:elm-border-none md:elm-rounded-none " +
                (hasFocusOrQuery ? "elm-shadow-md" : "")
              }
            >
              {hasFocusOrQuery ? (
                <button
                  tabIndex={0}
                  ref={cancelRef}
                  data-track-label="btn:search:cancel"
                  className="elm-pointer-events-auto elm-p-0 elm-h-9 elm-w-9 elm-flex-grow-0 elm-border-0 elm-bg-transparent elm-flex elm-justify-center elm-items-center elm-cursor-pointer lg:hover:elm-bg-gray-100 elm-rounded-full elm-transition-opacity elm-duration-300 elm-delay-150"
                  title={t("Cancel")}
                  aria-label={t("Cancel")}
                >
                  <BackIcon />
                </button>
              ) : (
                <button
                  tabIndex={0}
                  ref={searchRef}
                  data-track-label="btn:search:search"
                  className="elm-pointer-events-auto elm-p-0 elm-h-9 elm-w-9 elm-flex-grow-0 elm-border-0 elm-bg-transparent elm-flex elm-justify-center elm-items-center elm-cursor-pointer lg:hover:elm-bg-gray-100 elm-rounded-full elm-transition-opacity elm-duration-300 elm-delay-150"
                  title={t("Search")}
                  aria-label={t("Search")}
                >
                  <SearchIcon />
                </button>
              )}
              <div className="elm-flex-grow">
                <input
                  tabIndex={0}
                  ref={inputRef}
                  className="elm-w-full elm-border-transparent elm-leading-6 elm-text-base elm-outline-none elm-text-dd-gray-900"
                  type="text"
                  value={query}
                  placeholder={t("Search")}
                  spellCheck="false"
                  maxLength={150}
                  aria-label={t("Search")}
                  aria-placeholder={t("Search")}
                  autoCapitalize="off"
                  autoComplete="off"
                  autoCorrect="off"
                  onChange={onInputChange}
                  onFocus={onInputFocus}
                  onBlur={onInputBlur}
                  onKeyDown={onKeyDown}
                />
              </div>
              <button
                tabIndex={0}
                ref={clearRef}
                data-track-label="btn:search:clear"
                className={`lg:focus:elm-outline-none elm-pointer-events-auto elm-p-0 elm-h-9 elm-w-9 elm-flex-grow-0 elm-border-0 elm-bg-transparent elm-flex elm-justify-center elm-items-center elm-cursor-pointer lg:hover:elm-bg-gray-100 elm-rounded-full elm-transition-opacity elm-duration-300 elm-delay-150 ${hasQueryClass}`}
                title={t("Clear search")}
                aria-label={t("Clear search")}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  className="elm-block elm-fill-current elm-text-gray-500 elm-pointer-events-none"
                >
                  <path d="M0 0h24v24H0z" fill="none" />
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>

            {isHistoryVisible && <SearchHistory />}
            {hasQuery && (
              <SearchSuggestions suggestions={suggestions} searchText={query} />
            )}
          </div>
        </div>
      </div>
      {!hidePopularItems && <PopularItems />}
    </div>
  );
};
